import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _settings = null;
let _fetching = false;
const _listeners = [];

const fetchSettings = async () => {
  if (_settings || _fetching) return _settings;
  _fetching = true;
  try {
    const res = await fetch(`${BACKEND_URL}/api/cms/settings`);
    if (res.ok) {
      _settings = await res.json();
      _listeners.forEach(fn => fn(_settings));
    }
  } catch (e) { /* silent */ }
  _fetching = false;
  return _settings;
};

// Initialize Google Tag (gtag.js)
const initGoogleTag = (gadsId, ga4Id) => {
  if (document.getElementById('gtag-script')) return;
  const id = gadsId || ga4Id;
  if (!id) return;

  const s = document.createElement('script');
  s.id = 'gtag-script';
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  if (ga4Id) window.gtag('config', ga4Id);
  if (gadsId) window.gtag('config', gadsId);
};

// Initialize Meta Pixel
const initMetaPixel = (pixelId) => {
  if (!pixelId || window.fbq) return;
  window.fbq = function() {
    window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
  };
  window.fbq.queue = [];
  window.fbq.version = '2.0';
  window.fbq.loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
};

function TrackingPixels() {
  const location = useLocation();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchSettings().then(s => {
      if (!s) return;
      initGoogleTag(s.google_ads_id, s.google_analytics_id);
      initMetaPixel(s.meta_pixel_id);
      setLoaded(true);
    });
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!loaded) return;
    if (window.gtag) window.gtag('event', 'page_view', { page_path: location.pathname });
    if (window.fbq) window.fbq('track', 'PageView');
  }, [location.pathname, loaded]);

  return null;
}

// Tracking event functions for use across the app
export const trackEvent = {
  addToCart: (item) => {
    const value = item.price || 0;
    const name = item.name || '';
    if (window.gtag) {
      window.gtag('event', 'add_to_cart', {
        currency: 'INR', value,
        items: [{ item_name: name, price: value, quantity: 1 }]
      });
    }
    if (window.fbq) {
      window.fbq('track', 'AddToCart', { content_name: name, value, currency: 'INR' });
    }
  },

  purchase: (order) => {
    const value = order.total || 0;
    const orderId = order.order_number || order.id || '';
    if (window.gtag) {
      window.gtag('event', 'purchase', {
        transaction_id: orderId, value, currency: 'INR',
        items: (order.items || []).map(i => ({ item_name: i.name, price: i.price, quantity: i.quantity }))
      });
    }
    if (window.fbq) {
      window.fbq('track', 'Purchase', { value, currency: 'INR', content_type: 'product' });
    }
  },

  lead: (data) => {
    const name = data.serviceType || data.name || 'Service Booking';
    if (window.gtag) {
      window.gtag('event', 'generate_lead', { value: 0, currency: 'INR' });
    }
    if (window.fbq) {
      window.fbq('track', 'Lead', { content_name: name });
    }
  },

  viewProduct: (product) => {
    const value = product.price || product.discount_price || 0;
    if (window.gtag) {
      window.gtag('event', 'view_item', {
        currency: 'INR', value,
        items: [{ item_name: product.name, price: value }]
      });
    }
    if (window.fbq) {
      window.fbq('track', 'ViewContent', { content_name: product.name, value, currency: 'INR' });
    }
  },

  beginCheckout: (total) => {
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', { currency: 'INR', value: total });
    }
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', { value: total, currency: 'INR' });
    }
  }
};

export default TrackingPixels;
